import type { Session } from '@robotensor/api'
import { useSyncExternalStore } from 'react'
import { isProfessional, permissionsFor, tokenGrants } from './access.ts'
import { client } from './api.ts'

let restoring: Promise<Session | null> | null = null

/** Restores the session from the refresh cookie once per page load; later calls reuse the answer. */
export function ensureSession(): Promise<Session | null> {
  const s = client.getSession()
  if (s) return Promise.resolve(s)
  restoring ??= client.restore().catch(() => null).finally(() => {
    restoring = null
  })
  return restoring
}

function subscribe(cb: () => void) {
  return client.subscribe(() => cb())
}

export function useSession(): Session | null {
  return useSyncExternalStore(subscribe, () => client.getSession(), () => null)
}

/** The signed-in person (throws if used outside an authenticated route). */
export function useUser() {
  const s = useSession()
  if (!s) throw new Error('useUser outside an authenticated route')
  return s.user
}

export function usePermissions(): Set<string> {
  const s = useSession()
  return permissionsFor(s?.user.roles ?? [], tokenGrants(s?.accessToken))
}

export function onboardingComplete(onboarding: Record<string, unknown> | null | undefined): boolean {
  return !!onboarding && typeof onboarding.completed_at === 'string'
}

/** Professionals see technical detail (endpoints, codes); contributors see plain language. */
export function useTechnical(): boolean {
  const s = useSession()
  return isProfessional(s?.user.roles ?? [])
}
